import React, { useEffect, useRef, useState } from 'react';
import AppShell from '../components/layout/AppShell';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { authAPI, socialAPI, chatAPI, uploadsAPI, getAuthToken } from '../utils/api';
import { Search, UserPlus, Send, Check, X, MessageCircle, Circle, Smile, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const Friends = () => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const [user, setUser] = useState(null);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeFriend, setActiveFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [onlineIds, setOnlineIds] = useState(new Set());
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  const accent = isKidsMode ? 'amber' : 'teal';

  // ---- Initial load ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, fr, req, on] = await Promise.all([
          authAPI.getMe(),
          socialAPI.getFriends(),
          socialAPI.getFriendRequests(),
          chatAPI.onlineFriends().catch(() => ({ data: { online_ids: [] } })),
        ]);
        if (cancelled) return;
        setUser(u.data);
        setFriends(fr.data || []);
        setRequests(req.data || []);
        setOnlineIds(new Set(on.data?.online_ids || []));
      } catch {
        if (!cancelled) toast.error(lang === 'tr' ? 'Yüklenemedi' : 'Failed');
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  // ---- WebSocket ----
  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    const base = ((typeof window !== 'undefined' && window.__hafizim_backend_url) || process.env.REACT_APP_BACKEND_URL || '');
    const wsUrl = base.replace(/^http/, 'ws') + `/api/ws/chat?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (_) {
      return;
    }
    wsRef.current = ws;
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'message') {
          setMessages((prev) => {
            if (!activeFriend) return prev;
            const me = data.from_user_id;
            const peer = data.to_user_id;
            if (me === activeFriend.id || peer === activeFriend.id) {
              return [...prev, data];
            }
            return prev;
          });
          if (data.from_user_id !== user?.id && (!activeFriend || data.from_user_id !== activeFriend.id)) {
            toast.message(`💬 ${data.from_name}`, { description: data.text });
          }
        } else if (data.type === 'reaction') {
          setMessages((prev) => prev.map((m) => m.id === data.message_id ? { ...m, reactions: data.reactions } : m));
        }
      } catch (_) { /* noop */ }
    };
    ws.onerror = () => { /* silent */ };
    return () => { try { ws.close(); } catch (_) { /* noop */ } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeFriend?.id]);

  // ---- Load history when switching chat ----
  useEffect(() => {
    if (!activeFriend) return;
    let cancelled = false;
    chatAPI.history(activeFriend.id).then((r) => {
      if (!cancelled) setMessages(r.data?.messages || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [activeFriend]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const doSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const r = await socialAPI.searchUsers(searchQuery.trim());
      setSearchResults(r.data || []);
    } catch {
      toast.error(lang === 'tr' ? 'Arama başarısız' : 'Search failed');
    }
  };

  const sendRequest = async (uid) => {
    try {
      await socialAPI.sendFriendRequest(uid);
      toast.success(lang === 'tr' ? 'İstek gönderildi' : 'Request sent');
      setSearchResults((prev) => prev.filter((u) => u.id !== uid));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (lang === 'tr' ? 'Gönderilemedi' : 'Failed'));
    }
  };

  const accept = async (id) => {
    await socialAPI.acceptFriendRequest(id);
    setRequests((p) => p.filter((r) => r.id !== id));
    const fr = await socialAPI.getFriends();
    setFriends(fr.data || []);
    toast.success(lang === 'tr' ? 'Arkadaş olundu!' : 'Friends now!');
  };
  const reject = async (id) => {
    await socialAPI.rejectFriendRequest(id);
    setRequests((p) => p.filter((r) => r.id !== id));
  };

  const sendMessage = () => {
    if (!draft.trim() || !activeFriend || !wsRef.current) return;
    if (wsRef.current.readyState !== WebSocket.OPEN) {
      toast.error(lang === 'tr' ? 'Bağlantı kopuk, yeniden yükle' : 'Disconnected, reload');
      return;
    }
    const text = draft.trim().slice(0, 1000);
    wsRef.current.send(JSON.stringify({ to: activeFriend.id, text }));
    setDraft('');
  };

  const addReaction = async (messageId, emoji) => {
    try {
      const r = await chatAPI.react(messageId, emoji);
      // Optimistic — also covered by WS broadcast
      setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, reactions: r.data.reactions } : m));
    } catch (_) {
      toast.error(lang === 'tr' ? 'Reaksiyon eklenemedi' : 'Reaction failed');
    }
  };

  const REACTION_EMOJIS = ['👍', '❤️', '🤲', '🌙', '🔥', '😊'];

  const fileInputRef = useRef(null);
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activeFriend) return;
    if (!file.type.startsWith('image/')) {
      toast.error(lang === 'tr' ? 'Sadece resim' : 'Images only');
      return;
    }
    try {
      const r = await uploadsAPI.chatImage(file);
      const url = r.data?.url;
      if (!url) throw new Error('No url');
      // Build absolute URL for the recipient
      const base = ((typeof window !== 'undefined' && window.__hafizim_backend_url) || process.env.REACT_APP_BACKEND_URL || '');
      const absolute = base + url;
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ to: activeFriend.id, text: `[image]${absolute}` }));
      }
    } catch (_) {
      toast.error(lang === 'tr' ? 'Resim yüklenemedi' : 'Upload failed');
    }
  };

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Online Arkadaşlar' : 'Online Friends'}>
      <div className="max-w-6xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <header className="mb-2">
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{lang === 'tr' ? 'Topluluk' : 'Community'}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Online Arkadaşlar' : 'Online Friends'}
          </h1>
          <p className="text-stone-600 mt-2">
            {lang === 'tr' ? 'Arkadaş ekle, mesajlaş, beraber öğren.' : 'Add friends, chat, learn together.'}
          </p>
        </header>

        {/* Search box */}
        <section className="bg-white rounded-2xl border border-stone-200 p-5">
          <h2 className="text-sm uppercase tracking-[0.2em] text-stone-500 mb-3 font-semibold">
            {lang === 'tr' ? 'Arkadaş Bul' : 'Find Friends'}
          </h2>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                placeholder={lang === 'tr' ? 'İsim veya e-posta...' : 'Name or email...'}
                data-testid="friends-search-input"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40"
              />
            </div>
            <button type="button" onClick={doSearch} data-testid="friends-search-btn" className={`px-4 py-2.5 rounded-xl text-sm font-semibold text-white ${isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800'}`}>
              {lang === 'tr' ? 'Ara' : 'Search'}
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="mt-3 space-y-2">
              {searchResults.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-stone-50">
                  <div>
                    <p className="font-semibold text-stone-800 text-sm">{u.name}</p>
                    <p className="text-xs text-stone-500">{u.email}</p>
                  </div>
                  <button type="button" onClick={() => sendRequest(u.id)} data-testid={`friend-add-${u.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-stone-200 hover:border-stone-400 text-xs font-semibold">
                    <UserPlus className="w-3.5 h-3.5" /> {lang === 'tr' ? 'Ekle' : 'Add'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Pending requests */}
        {requests.length > 0 && (
          <section className="bg-white rounded-2xl border border-stone-200 p-5">
            <h2 className="text-sm uppercase tracking-[0.2em] text-stone-500 mb-3 font-semibold">
              {lang === 'tr' ? 'Gelen İstekler' : 'Pending Requests'}
            </h2>
            <div className="space-y-2">
              {requests.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-amber-50/60 border border-amber-200">
                  <div>
                    <p className="font-semibold text-stone-800 text-sm">{r.from_user?.name}</p>
                    <p className="text-xs text-stone-500">{r.from_user?.email}</p>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => accept(r.id)} data-testid={`request-accept-${r.id}`} className="p-2 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200">
                      <Check className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => reject(r.id)} data-testid={`request-reject-${r.id}`} className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Friends + Chat */}
        <section className="grid sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-stone-200 p-5 sm:col-span-1">
            <h2 className="text-sm uppercase tracking-[0.2em] text-stone-500 mb-3 font-semibold">
              {lang === 'tr' ? 'Arkadaşlarım' : 'My Friends'} ({friends.length})
            </h2>
            {friends.length === 0 ? (
              <p className="text-sm text-stone-500">{lang === 'tr' ? 'Henüz arkadaşın yok.' : 'No friends yet.'}</p>
            ) : (
              <div className="space-y-1.5">
                {friends.map((f) => {
                  const online = onlineIds.has(f.id);
                  const isActive = activeFriend?.id === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setActiveFriend(f)}
                      data-testid={`friend-item-${f.id}`}
                      className={`w-full text-left p-2.5 rounded-lg flex items-center gap-2 ${isActive ? (isKidsMode ? 'bg-amber-100' : 'bg-teal-50') : 'hover:bg-stone-50'}`}
                    >
                      <Circle className={`w-2 h-2 ${online ? 'text-emerald-500 fill-emerald-500' : 'text-stone-300 fill-stone-300'}`} />
                      <span className="flex-1 text-sm font-semibold text-stone-800 truncate">{f.name}</span>
                      <MessageCircle className="w-4 h-4 text-stone-400" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 sm:col-span-2 flex flex-col" style={{ minHeight: 420 }}>
            {!activeFriend ? (
              <div className="flex-1 flex items-center justify-center text-stone-400 text-sm">
                {lang === 'tr' ? 'Mesajlaşmak için bir arkadaş seç' : 'Pick a friend to chat'}
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-stone-100 flex items-center gap-3">
                  <Circle className={`w-2 h-2 ${onlineIds.has(activeFriend.id) ? 'text-emerald-500 fill-emerald-500' : 'text-stone-300 fill-stone-300'}`} />
                  <p className="font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{activeFriend.name}</p>
                </div>
                <div className="flex-1 p-4 space-y-2 overflow-y-auto" data-testid="chat-messages">
                  {messages.length === 0 && (
                    <p className="text-center text-xs text-stone-400 py-8">{lang === 'tr' ? 'İlk mesajı sen yaz 👇' : 'Send the first message 👇'}</p>
                  )}
                  {messages.map((m) => {
                    const mine = m.from_user_id === user?.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${mine ? (isKidsMode ? 'bg-amber-500 text-white rounded-br-sm' : 'bg-teal-700 text-white rounded-br-sm') : 'bg-stone-100 text-stone-800 rounded-bl-sm'}`}>
                          {m.text}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
                <div className="p-3 border-t border-stone-100 flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    data-testid="chat-image-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="chat-image-btn"
                    title={lang === 'tr' ? 'Resim gönder' : 'Send image'}
                    className="px-3 py-2.5 rounded-xl bg-white border border-stone-200 text-stone-600 hover:border-stone-400"
                  >
                    <ImageIcon className="w-4 h-4" />
                  </button>
                  <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder={lang === 'tr' ? 'Mesaj yaz...' : 'Type a message...'}
                    data-testid="chat-input"
                    maxLength={1000}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  />
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={!draft.trim()}
                    data-testid="chat-send"
                    className={`px-4 py-2.5 rounded-xl text-white ${isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800'} disabled:opacity-50`}
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </AppShell>
  );
};

export default Friends;
